/**
 * ============================================================================
 * TaskFlow ERP
 * Campaign Management
 * Prospect Import Mock Data
 * ============================================================================
 *
 * Purpose
 * -------
 * This file contains ONLY mock data.
 *
 * No business logic should be added here.
 *
 * The service layer (campaignImportService.js) will consume these objects.
 *
 * When backend APIs are implemented, this file can be removed without changing
 * any Page or Component.
 * ============================================================================
 */

/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/mock/campaignImportMock.js
 *
 * Description:
 * Mock data used by the Prospect Import feature.
 * Contains only static data.
 * No business logic should be implemented in this file.
 * ============================================================================
 */

/**
 * --------------------------------------------------------------------------
 * Status Master
 * --------------------------------------------------------------------------
 */

export const IMPORT_STATUS = {
    VERIFIED: "Verified",
    WARNING: "Warning",
    DUPLICATE: "Duplicate",
    INVALID: "Invalid"
};

/**
 * --------------------------------------------------------------------------
 * Validation Summary
 * --------------------------------------------------------------------------
 */

export const validationSummaryMock = {
    total_rows: 120,
    valid_rows: 108,
    invalid_rows: 4,
    duplicate_rows: 6,
    warning_rows: 2
};

/**
 * --------------------------------------------------------------------------
 * Import Records
 * --------------------------------------------------------------------------
 */

export const importRecordsMock = [
    {
        id: 1,
        row_no: 1,
        external_id: "EXT00001",
        name: "ABC Industries",
        contacts: 2,
        status: IMPORT_STATUS.VERIFIED,
        remarks: ""
    },
    {
        id: 2,
        row_no: 2,
        external_id: "EXT00002",
        name: "XYZ Technologies",
        contacts: 1,
        status: IMPORT_STATUS.DUPLICATE,
        remarks: "Prospect already exists"
    },
    {
        id: 3,
        row_no: 3,
        external_id: "EXT00003",
        name: "Global Traders",
        contacts: 3,
        status: IMPORT_STATUS.WARNING,
        remarks: "Industry not specified"
    },
    {
        id: 4,
        row_no: 4,
        external_id: "EXT00004",
        name: "Alpha Engineering",
        contacts: 2,
        status: IMPORT_STATUS.INVALID,
        remarks: "Invalid Email Address"
    },
    {
        id: 5,
        row_no: 5,
        external_id: "EXT00005",
        name: "Sunrise Pvt Ltd",
        contacts: 1,
        status: IMPORT_STATUS.VERIFIED,
        remarks: ""
    },
    {
        id: 6,
        row_no: 6,
        external_id: "EXT00006",
        name: "Prime Solutions",
        contacts: 4,
        status: IMPORT_STATUS.VERIFIED,
        remarks: ""
    },
    {
        id: 7,
        row_no: 7,
        external_id: "EXT00007",
        name: "Vision Systems",
        contacts: 2,
        status: IMPORT_STATUS.WARNING,
        remarks: "Lead Source missing"
    },
    {
        id: 8,
        row_no: 8,
        external_id: "EXT00008",
        name: "Evergreen Technologies",
        contacts: 2,
        status: IMPORT_STATUS.VERIFIED,
        remarks: ""
    },
    {
        id: 9,
        row_no: 9,
        external_id: "EXT00009",
        name: "Rapid Logistics",
        contacts: 1,
        status: IMPORT_STATUS.DUPLICATE,
        remarks: "Duplicate External ID"
    },
    {
        id: 10,
        row_no: 10,
        external_id: "EXT00010",
        name: "NextGen Manufacturing",
        contacts: 3,
        status: IMPORT_STATUS.VERIFIED,
        remarks: ""
    }
];

/**
 * --------------------------------------------------------------------------
 * Verify Response
 * --------------------------------------------------------------------------
 */

export const verifyResponseMock = {
    success: true,
    summary: validationSummaryMock,
    records: importRecordsMock
};

/**
 * --------------------------------------------------------------------------
 * Upload Response
 * --------------------------------------------------------------------------
 */

export const uploadResponseMock = {
    success: true,
    message: "File uploaded successfully.",
    upload_id: "UPLOAD-20260716-0001"
};

/**
 * --------------------------------------------------------------------------
 * Import Details
 * --------------------------------------------------------------------------
 */

export const importDetailsMock = {
    import_id: "UPLOAD-20260716-0001",
    file_name: "prospects_import.xlsx",
    file_size: "245 KB",
    uploaded_by: "Administrator",
    uploaded_on: "2026-07-16 10:30 AM",
    total_rows: 120,
    processed_rows: 120,
    status: "Verified"
};

/**
 * --------------------------------------------------------------------------
 * Compare Mock
 * --------------------------------------------------------------------------
 */

export const compareProspectMock = {
    existing: {
        company_name: "XYZ Technologies",
        industry: "Software",
        phone: "+91 9876543210",
        email: "sales@xyztech.com",
        city: "Bangalore",
        state: "Karnataka"
    },
    imported: {
        company_name: "XYZ Technologies",
        industry: "",
        phone: "+91 9876543210",
        email: "info@xyztech.com",
        city: "Bengaluru",
        state: "Karnataka"
    }
};

/**
 * --------------------------------------------------------------------------
 * Edit Prospect Mock
 * --------------------------------------------------------------------------
 */

export const editableProspectMock = {
    id: 3,
    company_name: "Global Traders",
    industry: "",
    lead_source: "",
    priority: "Medium",
    contacts: [
        {
            name: "John Smith",
            designation: "Manager",
            mobile: "9876543210",
            email: "john@globaltraders.com"
        }
    ],
    remarks: "Industry not specified"
};

/**
 * --------------------------------------------------------------------------
 * Progress Mock
 * --------------------------------------------------------------------------
 */

export const importProgressMock = [
    {
        percent: 10,
        message: "Uploading file..."
    },
    {
        percent: 30,
        message: "Reading spreadsheet..."
    },
    {
        percent: 55,
        message: "Validating records..."
    },
    {
        percent: 75,
        message: "Checking duplicates..."
    },
    {
        percent: 95,
        message: "Preparing import preview..."
    },
    {
        percent: 100,
        message: "Completed"
    }
];

/**
 * --------------------------------------------------------------------------
 * Standard Response Contract
 * --------------------------------------------------------------------------
 */

export const campaignImportMockResponse = {
    success: true,
    summary: validationSummaryMock,
    records: importRecordsMock
};

export default {
    IMPORT_STATUS,
    validationSummaryMock,
    importRecordsMock,
    verifyResponseMock,
    uploadResponseMock,
    importDetailsMock,
    compareProspectMock,
    editableProspectMock,
    importProgressMock,
    campaignImportMockResponse
};